/**
 * The score.
 *
 * The one place in this project that plays something it did not generate. Every
 * sound *effect* is synthesised at load time (R24) and a synthesised gunshot is
 * convincing; a synthesised score is not, so the music is two composed tracks
 * served from `public/music/` and the asset gate has a one-directory exception
 * for them.
 *
 * Two tracks, and which one is playing is driven by the match rather than by a
 * playlist: the quiet one while you are building, the loud one while you are
 * fighting. Crossfaded, because a hard cut announces the mechanism.
 *
 * **Nothing here touches the simulation.** It reads events and a combat counter
 * and produces sound, exactly like the rest of `src/audio/`.
 */

const TRACKS = {
  build: '/music/basebuilding.mp3',
  combat: '/music/main-theme.mp3',
} as const

export type TrackName = keyof typeof TRACKS

/** Seconds to fade between tracks. Long enough not to read as a cut. */
const CROSSFADE = 2.5

/**
 * Ticks of quiet before the score drops back to the building track. Combat is
 * bursty — a single exchange should not flip the music twice, and a lull inside
 * a battle should not either.
 */
const CALM_TICKS = 30 * 12

export class Music {
  private readonly elements = new Map<TrackName, HTMLAudioElement>()
  private current: TrackName | null = null
  private quietFor = CALM_TICKS
  private started = false

  /** Master switch, mirrored in the HUD and persisted across reloads. */
  enabled: boolean

  /** Overall level. Music sits under the effects, never over them. */
  volume = 0.38

  constructor(enabled = readPreference()) {
    this.enabled = enabled
  }

  /**
   * Load the tracks. Safe to call in an environment with no `Audio` element —
   * the headless harness and the tests have none, and the game should boot
   * silent rather than not at all.
   */
  load(): void {
    if (typeof document === 'undefined' || typeof window.Audio === 'undefined') return
    for (const [name, url] of Object.entries(TRACKS) as Array<[TrackName, string]>) {
      const el = new window.Audio(url)
      el.loop = true
      el.preload = 'auto'
      el.volume = 0
      this.elements.set(name, el)
    }
  }

  /**
   * Begin playback. Must be called from a user gesture: browsers refuse to
   * start audio otherwise, and a rejected promise here is a normal outcome
   * rather than an error worth surfacing.
   */
  start(): void {
    if (!this.enabled || this.started) return
    this.started = true
    this.switchTo('build')
  }

  setEnabled(on: boolean): void {
    this.enabled = on
    writePreference(on)
    if (!on) {
      for (const el of this.elements.values()) {
        el.pause()
        el.volume = 0
      }
      this.current = null
      this.started = false
      return
    }
    this.start()
  }

  /** Called on every fixed tick with whether anything is shooting. */
  update(inCombat: boolean): void {
    if (!this.enabled || !this.started) return
    if (inCombat) this.quietFor = 0
    else this.quietFor++
    const wanted: TrackName = this.quietFor < CALM_TICKS ? 'combat' : 'build'
    if (wanted !== this.current) this.switchTo(wanted)
  }

  dispose(): void {
    for (const el of this.elements.values()) {
      el.pause()
      el.src = ''
    }
    this.elements.clear()
    this.current = null
    this.started = false
  }

  private switchTo(name: TrackName): void {
    const next = this.elements.get(name)
    if (!next) return
    const previous = this.current ? this.elements.get(this.current) : null
    this.current = name

    void next.play().catch(() => {
      // Autoplay refused. `start()` runs again on the next gesture.
      this.started = false
    })
    fade(next, this.volume)
    if (previous && previous !== next) fade(previous, 0, () => previous.pause())
  }
}

/**
 * Linear volume ramp on a timer.
 *
 * `HTMLAudioElement` has no scheduled ramp of its own — that is a Web Audio
 * feature, and routing the element through the audio graph would mean the music
 * competes for the same voice budget the sound effects are capped by.
 */
function fade(el: HTMLAudioElement, to: number, done?: () => void): void {
  const from = el.volume
  const steps = Math.max(1, Math.round(CROSSFADE * 20))
  let step = 0
  const timer = window.setInterval(() => {
    step++
    const t = step / steps
    el.volume = Math.max(0, Math.min(1, from + (to - from) * t))
    if (step >= steps) {
      window.clearInterval(timer)
      done?.()
    }
  }, 50)
}

const STORAGE_KEY = 'bluealert.music'

function readPreference(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== 'off'
  } catch {
    return true
  }
}

function writePreference(on: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off')
  } catch {
    // Private browsing. The preference simply does not persist.
  }
}
